import { Clock, CheckCircle, XCircle, List, Filter, Video, Volume2, FileText, Code, ClipboardCheck, File } from "lucide-react";
import DepartmentSelector from "../../../../components/selectors/DepartmentSelector";

const hideScrollbarStyle = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

export type ReportStatusFilter = "all" | "COMPLETED" | "FAILED" | "PENDING";

export type ReportContentTypeFilter = "all" | "VIDEO" | "AUDIO" | "TEXT" | "FILE" | "CODE" | "EXAM";

export interface Department {
  id: string;
  name: string;
  color: string;
}

interface ReportFilterProps {
  selectedStatus: ReportStatusFilter;
  onStatusChange: (status: ReportStatusFilter) => void;

  selectedContentType: ReportContentTypeFilter;
  onContentTypeChange: (type: ReportContentTypeFilter) => void;

  selectedDepartment: string | null;
  onDepartmentChange: (departmentId: string | null) => void;
  departments: Department[];

  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;

  onClearFilters: () => void;
}

export default function ReportFilter({
  selectedStatus,
  onStatusChange,
  selectedContentType,
  onContentTypeChange,
  selectedDepartment,
  onDepartmentChange,
  departments,
  startDate,
  endDate,
  onDateChange,
  onClearFilters,
}: ReportFilterProps) {
  const statusOptions = [
    {
      key: "all" as ReportStatusFilter,
      label: "Tüm Raporlar",
      icon: List,
    },
    {
      key: "COMPLETED" as ReportStatusFilter,
      label: "Yapıldı",
      icon: CheckCircle,
    },
    {
      key: "FAILED" as ReportStatusFilter,
      label: "Yapılmadı",
      icon: XCircle,
    },
    {
      key: "PENDING" as ReportStatusFilter,
      label: "Bekleyen",
      icon: Clock,
    },
  ];

  const contentTypeOptions = [
    {
      key: "all" as ReportContentTypeFilter,
      label: "Tüm Türler",
      icon: List,
    },
    {
      key: "VIDEO" as ReportContentTypeFilter,
      label: "Video",
      icon: Video,
    },
    {
      key: "AUDIO" as ReportContentTypeFilter,
      label: "Ses",
      icon: Volume2,
    },
    {
      key: "TEXT" as ReportContentTypeFilter,
      label: "Metin",
      icon: FileText,
    },
    {
      key: "FILE" as ReportContentTypeFilter,
      label: "Dosya",
      icon: File,
    },
    {
      key: "CODE" as ReportContentTypeFilter,
      label: "Kod",
      icon: Code,
    },
    {
      key: "EXAM" as ReportContentTypeFilter,
      label: "Test",
      icon: ClipboardCheck,
    },
  ];

  const hasActiveFilters = () => {
    return selectedStatus !== "all" || selectedContentType !== "all" || selectedDepartment !== null || !!startDate || !!endDate;
  };

  return (
    <>
      <style>{hideScrollbarStyle}</style>
      <div
        className="pr-2 w-64 h-full overflow-y-auto hide-scrollbar"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-800">Filtreler</span>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters() && (
              <button onClick={onClearFilters} className="text-xs text-red-600 hover:text-red-700 font-medium" title="Filtreleri temizle">
                Temizle
              </button>
            )}
            <div className="flex items-center gap-1" />
          </div>
        </div>

        <div className="space-y-4">
          <div />

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Ödev Durumu</label>
            <div className="space-y-1">
              {statusOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedStatus === option.key;

                return (
                  <button
                    key={option.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(option.key);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                      isSelected ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-700 hover:bg-gray-100"
                    }`}>
                    <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
                    <span className="flex-1">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div />

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Departman</label>
            <DepartmentSelector
              value={selectedDepartment}
              onChange={(departmentId, department) => {
                onDepartmentChange(departmentId);
              }}
              placeholder="Tüm Departmanlar"
              allowClear={true}
              showColors={true}
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Tarih Aralığı</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={startDate || ""}
                  onChange={(e) => onDateChange(e.target.value || null, endDate)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={endDate || ""}
                  onChange={(e) => onDateChange(startDate, e.target.value || null)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
